import {
	ServiceScheduleType,
	useLazyWorkplaceServiceScheduleQuery,
} from '../../../graphql/graphql';
import { EVENT_PRESET } from '../../Workplace/Conts/eventPreset.const';
import { TimelineCalendarEvent } from '../../Calendar/Components/TimelineCalendar/TimelineCalendar';
import { Moment } from 'moment';
import { CalendarView } from '../../Calendar/Enums/CalendarView.eunm';
import { prepareDateTimeWithOriginDst } from '../../Base/Helpers/dateTime.helper';
import { getMomentDate } from '../../../Utils/dates.util';
import { useState } from 'react';
import moment from 'moment';

type TimelineEventOptions<T> = {
	from: Moment;
	to: Moment;
	workplaceIds?: string[];
	types: ServiceScheduleType[];
	calendarView?: CalendarView;
	getResources: (item: any) => T[];
	getResourceId: (resource: T) => string;
	getTitle: (args: {
		timeFrom: Moment;
		timeTo: Moment;
		resource: T;
		calendarView?: CalendarView;
		type: ServiceScheduleType;
	}) => string;
	getTooltipText: (timeFrom: Moment, timeTo: Moment, resource: T) => string;
	getResourceTitle?: (resource: T) => string;
	customResourceId?: string;
	order?: number;
};

export function useServiceScheduleAsTimeline() {
	const [getServiceSchedules] = useLazyWorkplaceServiceScheduleQuery();
	const [isLoading, setIsLoading] = useState(false);

	//FC TO LOAD SERVICE SCHEDULE AND RETURN THEM AS EVENTS FOR TIMELINE CALENDAR
	const getServiceSchedulesAsTimeline = async <T>({
		from,
		to,
		workplaceIds,
		types,
		calendarView,
		getResources,
		getResourceId,
		getTitle,
		getTooltipText,
		getResourceTitle,
		customResourceId,
		order,
	}: TimelineEventOptions<T>): Promise<TimelineCalendarEvent[]> => {
		setIsLoading(true);
		const tempEvents: TimelineCalendarEvent[] = [];

		const data = await getServiceSchedules({
			from: from.format(),
			to: to.format(),
			workplaceIds,
			types,
			includeOccurrences: true,
		});

		data?.data?.serviceSchedules?.data?.forEach(serviceSchedule => {
			serviceSchedule?.serviceScheduleItems?.forEach(serviceScheduleItem => {
				if (serviceScheduleItem?.id) {
					const serviceScheduleType: ServiceScheduleType =
						serviceSchedule?.type ?? ServiceScheduleType.Default;

					const resources = getResources(serviceScheduleItem);

					resources.forEach(resource => {
						const baseEventData = {
							id: serviceScheduleItem.id,
							backgroundColor: EVENT_PRESET[serviceScheduleType]?.eventColor,
							borderColor: EVENT_PRESET[serviceScheduleType]?.eventColor,
							textColor: '#000',
							resourceId: customResourceId ?? getResourceId(resource),
						};

						const occurrences = serviceScheduleItem?.occurrences;

						if (Array.isArray(occurrences) && occurrences.length > 0) {
							occurrences.forEach(occurrence => {
								const timeFrom = prepareDateTimeWithOriginDst(
									serviceSchedule.dateFrom,
									serviceScheduleItem.timeFrom,
									occurrence,
									serviceScheduleItem.timeFrom,
								);
								const timeTo = prepareDateTimeWithOriginDst(
									serviceSchedule.dateFrom,
									serviceScheduleItem.timeFrom,
									occurrence,
									serviceScheduleItem.timeTo,
								);

								const duration = moment.duration(
									getMomentDate(timeTo).diff(getMomentDate(timeFrom)),
								);

								tempEvents.push({
									...baseEventData,
									start: moment.utc(timeFrom).format(),
									end: moment
										.utc(timeFrom)
										.add(duration.asSeconds(), 'seconds')
										.format(),
									title: getTitle({
										calendarView,
										timeFrom,
										timeTo,
										resource,
										type: serviceScheduleType,
									}),
									extendedProps: {
										serviceSchedule,
										serviceScheduleItem,
										timeFrom,
										timeTo,
										tooltipText: getTooltipText(timeFrom, timeTo, resource),
										resourceId: customResourceId ?? getResourceId(resource),
										resourceTitle: getResourceTitle?.(resource),
										order,
									},
								});
							});
						}
					});
				}
			});
		});

		setIsLoading(false);
		return tempEvents;
	};

	return {
		getServiceSchedulesAsTimeline,
		isLoading,
	};
}
